import React, { Component } from 'react';
import { connect } from 'react-redux';
import Modal from 'react-responsive-modal';
import _ from 'underscore';
import FileSaver from 'file-saver';
import { SSelect, SAlert, SLoader, SIcon } from '@avalara/skylab-sdk/react';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';
import { TabsContainer, TabsPanel } from '../tabs/TabsContainer';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import Logger, { LOG_LEVELS } from '../../utils/logger';
import { withFullStoryExclude } from '../../utils/fullStoryHelper';
import CLIENT from '../../utils/restClient';
import { STATUS_ROW_TYPE } from '../../utils/nlDigipoortV2Mock';
import SubmissionStatusTable from './nlDigipoortV2/SubmissionStatusTable';
import UserTransactionHistory from './nlDigipoortV2/UserTransactionHistory';

const FORM_NAME = 'NL_SUBMIT_FILING_V2';
const log = new Logger(FORM_NAME, LOG_LEVELS.DEBUG);
const MODAL_CLASSNAMES = {
  modal: 'modal-full-width-height modal-max-width-85 nl-digipoort-v2-modal',
  closeIcon: 'nl-digipoort-v2-close-icon',
};
const MAX_PASSWORD_LENGTH = 100;
const REPORT_TYPE = Object.freeze({ VAT: 'vat', ECL: 'ecl' });

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) {
    return '';
  }
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

// Digipoort status codes: '500' is the final accepted state, '420' is a technical
// problem; both classifications mirror the legacy NLSubmitFiling.determineFilingStatus.
const DIGIPOORT_STATUS_CODE = Object.freeze({ Success: '500', TechnicalProblem: '420' });

// HTTP status returned by getNLSubmissionStatus (the pre-check called before opening the
// modal) when this filing has never been submitted before - not an error, just "fresh".
const PRECHECK_HTTP_STATUS = Object.freeze({ NotFound: 404 });

function getStatusRowType(row) {
  if (row.errorCode || row.statusCode === DIGIPOORT_STATUS_CODE.TechnicalProblem) {
    return STATUS_ROW_TYPE.Error;
  }
  if (row.statusCode === DIGIPOORT_STATUS_CODE.Success) {
    return STATUS_ROW_TYPE.Success;
  }
  return STATUS_ROW_TYPE.Info;
}

function mapSendToDigipoortStatusRows(statusRows) {
  return (statusRows || []).map((row, index) => ({
    id: index,
    code: row.statusCode,
    description: row.statusDescription,
    timestamp: row.timestamp,
    errorCode: row.errorCode,
    errorDescription: row.errorDescription,
    type: getStatusRowType(row),
  }));
}

const LOGIN_PHASE = Object.freeze({ Idle: 'idle', LoggingIn: 'loggingIn', Connected: 'connected' });
const XBRL_PHASE = Object.freeze({ Idle: 'idle', Generating: 'generating', Ready: 'ready' });
const SUBMIT_PHASE = Object.freeze({ Idle: 'idle', Submitting: 'submitting', Submitted: 'submitted' });

const DROPDOWN_VALUES = Object.freeze({
  taxonomyIdentifier: ['Omzetbelasting', 'ICP', 'Suppletie', 'OSS'],
  identityRole: ['Intermediair', 'Servicebureau', 'Verantwoordingsplichtige'],
  taxonomyIdentifierUri: [
    'http://www.nltaxonomie.nl/nt20/bd/20251210/entrypoints/bd-rpt-ob-aangifte-2026.xsd',
    'http://www.nltaxonomie.nl/nt20/bd/20251210/entrypoints/bd-rpt-ob-suppletie-2026.xsd',
    'http://www.nltaxonomie.nl/nt20/bd/20251210/entrypoints/bd-rpt-icp-opgaaf-2026.xsd',
    'http://www.nltaxonomie.nl/nt19/bd/20241211/entrypoints/bd-rpt-ob-aangifte-2025.xsd',
    'http://www.nltaxonomie.nl/nt19/bd/20241211/entrypoints/bd-rpt-ob-suppletie-2025.xsd',
    'http://www.nltaxonomie.nl/nt19/bd/20241211/entrypoints/bd-rpt-icp-opgaaf-2025.xsd',
    'http://www.nltaxonomie.nl/nt15/bd/20210915/entrypoints/bd-rpt-oss.xsd',
  ],
});

class NLSubmissionFilingV2 extends Component {

  constructor(props) {
    super(props);
    this.reportType = this.props.isVATFiling ? REPORT_TYPE.VAT : REPORT_TYPE.ECL;
    this.state = {
      isModalOpen: false,
      digiPortUserName: '',
      digiPortPassword: '',
      loginPhase: LOGIN_PHASE.Idle,
      loginErrorMessage: null,
      xbrlPhase: XBRL_PHASE.Idle,
      xbrlErrorMessage: null,
      xbrlResult: null,
      submitPhase: SUBMIT_PHASE.Idle,
      submitErrorMessage: null,
      statusRows: [],
      transactionId: null,
      markedAsSubmitted: false,
      activeTab: 'status',
      tabsResetKey: 0,
    };
  }

  componentDidMount() {
    this.resetDropdowns();
  }

  resetDropdowns() {
    const filteredTaxonomyIdentifierUri = this.getFilteredTaxonomyIdentifierUri(DROPDOWN_VALUES.taxonomyIdentifier[0]);
    this.setState({
      filteredTaxonomyIdentifierUri,
      selectedTaxonomyIdentifierUri: filteredTaxonomyIdentifierUri[0],
      selectedTaxonomyIdentifier: DROPDOWN_VALUES.taxonomyIdentifier[0],
      selectedIdentityRole: DROPDOWN_VALUES.identityRole[2],
    });
  }

  getFilteredTaxonomyIdentifierUri(taxonomyFilter) {
    const matchers = {
      Omzetbelasting: 'aangifte',
      ICP: 'opgaaf',
      Suppletie: 'suppletie',
      OSS: 'oss',
    };
    return _.filter(DROPDOWN_VALUES.taxonomyIdentifierUri, (uri) => uri.includes(matchers[taxonomyFilter]));
  }

  onOpenModal = () => {
    // Before showing anything, check whether this filing was already accepted by
    // Digipoort on a previous attempt (e.g. the user closed the window before seeing
    // the result). Mirrors legacy NLSubmitFiling.onOpenModal/setFilingStatusBeforeSubmit:
    // an already-accepted (500) filing is auto-approved with no further confirmation,
    // the modal never opens.
    CLIENT.getNLSubmissionStatus(this.reportType, this.props.vatnumber, this.props.fromDate, this.props.toDate)
      .then((response) => {
        if (response && response.status === PRECHECK_HTTP_STATUS.NotFound) {
          // Never submitted before - open the normal login/xbrl/send flow.
          this.openDigipoortModal();
          return;
        }
        const hasCode500 = response && String(response.statusCode) === DIGIPOORT_STATUS_CODE.Success && !response.errorCode;
        if (hasCode500) {
          this.setState({ isModalOpen: false });
          this.props.onApprove();
        } else {
          this.openDigipoortModal();
        }
      })
      .catch((error) => {
        log.debug('getNLSubmissionStatus error: ', error);
        this.openDigipoortModal();
      });
  };

  openDigipoortModal = () => {
    this.setState({
      isModalOpen: true,
      digiPortUserName: '',
      digiPortPassword: '',
      loginPhase: LOGIN_PHASE.Idle,
      loginErrorMessage: null,
      xbrlPhase: XBRL_PHASE.Idle,
      xbrlErrorMessage: null,
      xbrlResult: null,
      submitPhase: SUBMIT_PHASE.Idle,
      submitErrorMessage: null,
      statusRows: [],
      markedAsSubmitted: false,
      activeTab: 'status',
    });
  };

  onCloseModal = () => {
    // If we're logged into Digipoort, close the cached session first.
    if (this.state.loginPhase === LOGIN_PHASE.Connected) {
      CLIENT.closeDigipoortSubmission({
        digipoortUserName: this.state.digiPortUserName,
        digipoortPassword: this.state.digiPortPassword,
      })
        .catch((err) => log.debug('closeDigipoortSubmission error: ', err))
        .finally(() => this.setState({ isModalOpen: false, loginPhase: LOGIN_PHASE.Idle }));
      return;
    }
    this.setState({ isModalOpen: false });
  };

  userNameFieldChange = (evt) => {
    this.setState({ digiPortUserName: evt.target.value });
  };

  onPasswordFieldChange = (evt) => {
    this.setState({ digiPortPassword: evt.target.value });
  };

  dropdownSelectionChangeHandler(name) {
    return (e) => {
      const selectedValue = e.detail.item.value;
      if (name === 'IdentityRole') {
        this.setState({ selectedIdentityRole: selectedValue });
      }
      if (name === 'TaxonomyIdentifier') {
        const filteredTaxonomyIdentifierUri = this.getFilteredTaxonomyIdentifierUri(selectedValue);
        this.setState({
          selectedTaxonomyIdentifier: selectedValue,
          filteredTaxonomyIdentifierUri,
          selectedTaxonomyIdentifierUri: filteredTaxonomyIdentifierUri[0],
        });
      }
      if (name === 'TaxonomyIdentifierUri') {
        this.setState({ selectedTaxonomyIdentifierUri: selectedValue });
      }
    };
  }

  getDropDownComponent(name, data) {
    if (!data) {
      return <div></div>;
    }
    const optionsList = data.map((key) => ({
      key,
      value: key,
      label: key,
      selected: this.state['selected' + name] === key,
    }));
    return (
      <SSelect
        inputId={`nl-submit-v2-${name.toLowerCase()}`}
        name={name}
        optionsList={optionsList}
        multiple={false}
        onS-select={this.dropdownSelectionChangeHandler(name)}
      />
    );
  }

  handleLogin = () => {
    const { digiPortUserName, digiPortPassword } = this.state;
    if (_.isEmpty((digiPortUserName || '').trim())) {
      this.setState({ loginErrorMessage: LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_BlankUsername') });
      return;
    }
    if (_.isEmpty(digiPortPassword) || digiPortPassword.length > MAX_PASSWORD_LENGTH) {
      this.setState({ loginErrorMessage: LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_BlankPassword') });
      return;
    }

    this.setState({ loginPhase: LOGIN_PHASE.LoggingIn, loginErrorMessage: null });
    CLIENT.loginToDigipoort({
      digipoortUserName: digiPortUserName,
      digipoortPassword: digiPortPassword,
    })
      .then(() => {
        this.setState({ loginPhase: LOGIN_PHASE.Connected });
      })
      .catch((error) => {
        log.debug('loginToDigipoort error: ', error);
        this.setState({
          loginPhase: LOGIN_PHASE.Idle,
          loginErrorMessage: LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_LoginFailed'),
        });
      });
  };

  handleGenerateXbrl = () => {
    this.setState({ xbrlPhase: XBRL_PHASE.Generating, xbrlErrorMessage: null });
    CLIENT.generateXBRL({
      reportFileURL: this.props.uri.split('/').pop(),
      reportFileName: `${this.props.filename}.${this.props.documentExtension}`,
      reportType: this.reportType,
      taxonomyURI: this.state.selectedTaxonomyIdentifierUri,
      digipoortUserName: this.state.digiPortUserName,
      digipoortPassword: this.state.digiPortPassword,
    })
      .then((response) => {
        const xbrlContent = atob(response.xbrlDocument);
        const auditReportContent = atob(response.auditReportContent);
        this.setState({
          xbrlPhase: XBRL_PHASE.Ready,
          xbrlResult: {
            xbrlFileName: `${this.props.filename}.xbrl`,
            auditReportFileName: `AuditReport-${this.props.filename}.html`,
            sizeLabel: formatFileSize(xbrlContent.length),
            xbrlContent,
            xbrlDocument: response.xbrlDocument,
            auditReportContent,
          },
        });
      })
      .catch((error) => {
        log.debug('generateXBRL error: ', error);
        this.setState({
          xbrlPhase: XBRL_PHASE.Idle,
          xbrlErrorMessage: LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_GenerateXbrlFailed'),
        });
      });
  };

  handleSend = () => {
    // Sending a filing always concerns the submission status, so jump the user
    // there even if they were browsing the transaction history tab.
    this.setState((prevState) => ({
      submitPhase: SUBMIT_PHASE.Submitting,
      submitErrorMessage: null,
      activeTab: 'status',
      tabsResetKey: prevState.tabsResetKey + 1,
    }));
    CLIENT.sendToDigipoort({
      reportFileURL: this.props.uri.split('/').pop(),
      reportFileName: `${this.props.filename}.${this.props.documentExtension}`,
      reportType: this.reportType,
      vatNumber: this.props.vatnumber,
      identityRole: this.state.selectedIdentityRole,
      taxonomyIdentifier: this.state.selectedTaxonomyIdentifier,
      taxonomyURI: this.state.selectedTaxonomyIdentifierUri,
      xbrlDocument: this.state.xbrlResult.xbrlDocument,
      fromDate: this.props.fromDate,
      toDate: this.props.toDate,
      digipoortUserName: this.state.digiPortUserName,
      digipoortPassword: this.state.digiPortPassword,
    })
      .then((response) => {
        // Show the status tab with the resulting rows; when the response already
        // includes the accepted (500) status, renderSubmissionStatusTab surfaces
        // the "Mark as Submitted" action so the user can confirm it here.
        this.setState({
          submitPhase: SUBMIT_PHASE.Submitted,
          statusRows: mapSendToDigipoortStatusRows(response.statusRows),
          transactionId: response.kenmerk || null,
          activeTab: 'status',
        });
      })
      .catch((error) => {
        log.debug('sendToDigipoort error: ', error);
        this.setState({
          submitPhase: SUBMIT_PHASE.Idle,
          submitErrorMessage: LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_SendFailed'),
        });
      });
  };

  handleDownloadXbrl = () => {
    const { xbrlResult } = this.state;
    const xbrlBlob = new Blob([xbrlResult.xbrlContent], { type: 'application/xml' });
    FileSaver.saveAs(xbrlBlob, xbrlResult.xbrlFileName);
    const auditReportBlob = new Blob([xbrlResult.auditReportContent], { type: 'text/plain' });
    FileSaver.saveAs(auditReportBlob, xbrlResult.auditReportFileName);
  };

  handleMarkAsSubmitted = () => {
    this.setState({ markedAsSubmitted: true });
    this.props.onApprove();
  };

  onTabChange = (tabNumber) => {
    this.setState({ activeTab: tabNumber === 2 ? 'history' : 'status' });
  };

  getEntityChipValue() {
    return this.props.companyName || this.props.vatnumber;
  }

  getPeriodChipValue() {
    const { periodName, fromDate, toDate } = this.props;
    return periodName ? `${periodName} (${fromDate} – ${toDate})` : `${fromDate} – ${toDate}`;
  }

  getTaxonomyChipValue() {
    const uri = this.state.selectedTaxonomyIdentifierUri || '';
    const fileName = uri.split('/').pop() || '';
    return fileName.replace('.xsd', '');
  }

  renderLoginSection() {
    const { loginPhase, loginErrorMessage } = this.state;
    const isConnected = loginPhase === LOGIN_PHASE.Connected;
    const isLoggingIn = loginPhase === LOGIN_PHASE.LoggingIn;

    let loginButtonClassName = 'button primary';
    if (isConnected) {
      loginButtonClassName = 'button nl-digipoort-v2-connected-btn';
    } else if (isLoggingIn) {
      loginButtonClassName = 'button primary disabled';
    }

    let loginButtonContent;
    if (isLoggingIn) {
      loginButtonContent = <LanguageStringResolver string="submitFilingNLV2_LoggingIn" />;
    } else if (isConnected) {
      loginButtonContent = (
        <>
          <SIcon name="check-circle" className="mr5" style={{ verticalAlign: 'middle' }} />
          <LanguageStringResolver string="submitFilingNLV2_Connected" />
        </>
      );
    } else {
      loginButtonContent = (
        <>
          <SIcon name="key" className="mr5" style={{ verticalAlign: 'middle' }} />
          <LanguageStringResolver string="submitFilingNLV2_Login" />
        </>
      );
    }

    return (
      <fieldset>
        {loginErrorMessage && (
          <SAlert status="error" className="mb10" noDismiss>
            <span>{loginErrorMessage}</span>
          </SAlert>
        )}
        <label className="mt10">
          <LanguageStringResolver string="submitFilingNL_Username" />{': '}
        </label>
        <input
          onChange={this.userNameFieldChange}
          name="digiportUsername"
          value={this.state.digiPortUserName}
          disabled={isConnected}
        />
        <label className="mt10">
          <LanguageStringResolver string="submitFilingNL_Password" />{': '}
        </label>
        <input
          type="password"
          className={withFullStoryExclude()}
          onChange={this.onPasswordFieldChange}
          name="digiportPassword"
          value={this.state.digiPortPassword}
          disabled={isConnected}
        />
        <div className="nl-digipoort-v2-login-row mt10 mb10">
          <a
            className={loginButtonClassName}
            onClick={isConnected || isLoggingIn ? undefined : this.handleLogin}
          >
            {loginButtonContent}
          </a>
          {isLoggingIn && <SLoader loading className="small ml10" style={{ display: 'inline-flex', verticalAlign: 'middle' }} />}
          {isConnected && (
            <span className="ml10">
              <LanguageStringResolver string="submitFilingNLV2_AuthenticatedAs" params={[this.state.digiPortUserName]} />
            </span>
          )}
        </div>

        <label className="mt10">
          <LanguageStringResolver string="submitFilingNL_IdentityRole" />{': '}
        </label>
        {this.getDropDownComponent('IdentityRole', DROPDOWN_VALUES.identityRole)}
        <label className="mt10">
          <LanguageStringResolver string="submitFilingNL_TaxonomyIdentifier" />{': '}
        </label>
        {this.getDropDownComponent('TaxonomyIdentifier', DROPDOWN_VALUES.taxonomyIdentifier)}
        <label className="mt10">
          <LanguageStringResolver string="submitFilingNL_TaxonomyIdentifierUri" />{': '}
        </label>
        {this.getDropDownComponent('TaxonomyIdentifierUri', this.state.filteredTaxonomyIdentifierUri)}

        <SAlert status="warning" className="mt10">
          <span><LanguageStringResolver string="submitFilingNL_StaticWarning" /></span>
        </SAlert>
      </fieldset>
    );
  }

  renderGenerateXbrlSection() {
    const isConnected = this.state.loginPhase === LOGIN_PHASE.Connected;
    const { xbrlPhase, xbrlErrorMessage, xbrlResult } = this.state;

    return (
      <>
        <hr className="mt20 mb20" />
        <fieldset>
          <legend>
            <SIcon name="folder" className="mr5" style={{ verticalAlign: 'middle' }} />
            <LanguageStringResolver string="submitFilingNLV2_GenerateXbrlSectionTitle" />
          </legend>
          <s-row>
            <s-col span="4">
              <div className="nl-digipoort-v2-info-chip">
                <div className="nl-digipoort-v2-info-chip-label"><LanguageStringResolver string="submitFilingNLV2_Entity" /></div>
                <div className="bold">{this.getEntityChipValue()}</div>
              </div>
            </s-col>
            <s-col span="4">
              <div className="nl-digipoort-v2-info-chip">
                <div className="nl-digipoort-v2-info-chip-label"><LanguageStringResolver string="submitFilingNLV2_Period" /></div>
                <div className="bold">{this.getPeriodChipValue()}</div>
              </div>
            </s-col>
            <s-col span="4">
              <div className="nl-digipoort-v2-info-chip">
                <div className="nl-digipoort-v2-info-chip-label"><LanguageStringResolver string="submitFilingNLV2_Taxonomy" /></div>
                <div className="bold">{this.getTaxonomyChipValue()}</div>
              </div>
            </s-col>
          </s-row>

          {xbrlErrorMessage && (
            <SAlert status="error" className="mt10 mb10" noDismiss>
              <span>{xbrlErrorMessage}</span>
            </SAlert>
          )}

          {xbrlPhase === XBRL_PHASE.Ready ? (
            <div className="nl-digipoort-v2-xbrl-result mt10">
              <div className="nl-digipoort-v2-xbrl-result-icon">
                <SIcon name="folder" />
              </div>
              <div className="nl-digipoort-v2-xbrl-result-info">
                <div className="bold">{xbrlResult.auditReportFileName} + {xbrlResult.xbrlFileName}</div>
                <div className="nl-digipoort-v2-info-chip-label">{xbrlResult.sizeLabel} · {this.getTaxonomyChipValue()}</div>
              </div>
              <a className="button primary" onClick={this.handleDownloadXbrl}>
                <SIcon name="download" className="mr5" style={{ verticalAlign: 'middle' }} />
                <LanguageStringResolver string="submitFilingNLV2_DownloadXbrlAndAuditReport" />
              </a>
            </div>
          ) : (
            <div className="mt10">
              <a
                className={!isConnected || xbrlPhase === XBRL_PHASE.Generating ? 'button primary disabled' : 'button primary'}
                onClick={isConnected && xbrlPhase !== XBRL_PHASE.Generating ? this.handleGenerateXbrl : undefined}
              >
                {xbrlPhase === XBRL_PHASE.Generating ? (
                  <LanguageStringResolver string="submitFilingNLV2_Generating" />
                ) : (
                  <>
                    <SIcon name="wrench-2" className="mr5" style={{ verticalAlign: 'middle' }} />
                    <LanguageStringResolver string="submitFilingNLV2_GenerateXbrlAndAuditReport" />
                  </>
                )}
              </a>
              {xbrlPhase === XBRL_PHASE.Generating && <SLoader loading className="small ml10" style={{ display: 'inline-flex', verticalAlign: 'middle' }} />}
            </div>
          )}
        </fieldset>
      </>
    );
  }

  renderSendSection() {
    const canSend = this.state.xbrlPhase === XBRL_PHASE.Ready;
    const { submitPhase, submitErrorMessage } = this.state;
    const isSubmitting = submitPhase === SUBMIT_PHASE.Submitting;
    const isSubmitted = submitPhase === SUBMIT_PHASE.Submitted;

    let sendButtonContent;
    if (isSubmitting) {
      sendButtonContent = <LanguageStringResolver string="submitFilingNLV2_Sending" />;
    } else if (isSubmitted) {
      sendButtonContent = (
        <>
          <SIcon name="check-circle" className="mr5" style={{ verticalAlign: 'middle' }} />
          <LanguageStringResolver string="submitFilingNLV2_Sent" />
        </>
      );
    } else {
      sendButtonContent = (
        <>
          <SIcon name="send" className="mr5" style={{ verticalAlign: 'middle' }} />
          <LanguageStringResolver string="submitFilingNLV2_SendToDigipoort" />
        </>
      );
    }

    return (
      <>
        <hr className="mt20 mb20" />
        <div>
          {submitErrorMessage && (
            <SAlert status="error" className="mb10" noDismiss>
              <span>{submitErrorMessage}</span>
            </SAlert>
          )}
          <a
            className={!canSend || isSubmitting || isSubmitted ? 'button primary disabled' : 'button primary'}
            onClick={canSend && !isSubmitting && !isSubmitted ? this.handleSend : undefined}
          >
            {sendButtonContent}
          </a>
          {isSubmitting && <SLoader loading className="small ml10" style={{ display: 'inline-flex', verticalAlign: 'middle' }} />}
        </div>
      </>
    );
  }

  renderSubmissionStatusTab() {
    const { submitPhase, statusRows, markedAsSubmitted } = this.state;
    if (submitPhase !== SUBMIT_PHASE.Submitted) {
      return (
        <div className="nl-digipoort-v2-empty-state mt10">
          <div className="nl-digipoort-v2-empty-state-icon">
            <SIcon name="send" />
          </div>
          <p className="bold nl-digipoort-v2-empty-state-title">
            <LanguageStringResolver string="submitFilingNLV2_NoSubmissionYetTitle" />
          </p>
          <p className="nl-digipoort-v2-empty-state-subtitle">
            <LanguageStringResolver string="submitFilingNLV2_NoSubmissionYetSubtitlePrefix" />
            {' '}
            <strong><LanguageStringResolver string="submitFilingNLV2_SendToDigipoort" /></strong>
            {' '}
            <LanguageStringResolver string="submitFilingNLV2_NoSubmissionYetSubtitleSuffix" />
          </p>
        </div>
      );
    }

    const hasCode500 = statusRows.some((row) => row.code === '500');

    return (
      <div className="mt10">
        <SubmissionStatusTable rows={statusRows} />
        {hasCode500 && !markedAsSubmitted && (
          <a className="button primary mt10" onClick={this.handleMarkAsSubmitted}>
            <LanguageStringResolver string="submitFilingNLV2_MarkAsSubmitted" />
          </a>
        )}
      </div>
    );
  }

  render() {
    const isConnected = this.state.loginPhase === LOGIN_PHASE.Connected;

    return (
      <>
        <ReadOnlyWrapper>
          <button className="primary mb20" onClick={this.onOpenModal}>
            {this.props.isVATFiling ? (
              <LanguageStringResolver string="submitFilingNL_VR_Submit" />
            ) : (
              <LanguageStringResolver string="submitFilingNL_ECL_Submit" />
            )}
          </button>
        </ReadOnlyWrapper>
        <Modal center classNames={MODAL_CLASSNAMES} open={this.state.isModalOpen} onClose={this.onCloseModal}>
          <div>
            <div className="nl-digipoort-v2-title-bar">
              <h2><LanguageStringResolver string="submitFilingNL_Title" /></h2>
            </div>

            {this.renderLoginSection()}
            {this.renderGenerateXbrlSection()}
            {this.renderSendSection()}

            {isConnected && (
              <div className="mt20">
                <TabsContainer key={this.state.tabsResetKey} onTabChange={this.onTabChange}>
                  <TabsPanel label={LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_SubmissionStatusTab')} selected="true">
                        {this.renderSubmissionStatusTab()}
                  </TabsPanel>
                  <TabsPanel label={LANGUAGES_CONTROLLER.format(this.props.languages, 'submitFilingNLV2_UserTransactionHistoryTab')}>
                    <div className="mt10">
                      {this.state.activeTab === 'history' && (
                        <UserTransactionHistory
                          digipoortUserName={this.state.digiPortUserName}
                          digipoortPassword={this.state.digiPortPassword}
                        />
                      )}
                    </div>
                  </TabsPanel>
                </TabsContainer>
              </div>
            )}

            <a className="button secondary mt20" onClick={this.onCloseModal}>
              <LanguageStringResolver string="submitFiling_cancel" />
            </a>
          </div>
        </Modal>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    languages: state.languages,
  };
}


NLSubmissionFilingV2.defaultProps = {
  uri: '',
  onApprove: () => {},
  filename: '',
};

export default connect(mapStateToProps)(NLSubmissionFilingV2);
